import {
  promiseState,
  useBooleanState,
  useDataStream,
  useNavigator
} from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useState } from 'react';
import { switchMap } from 'rxjs/operators';
import { viewTexts } from '../../../constants/view-texts';
import { useListFiltered } from '../../../functions/hooks/use-list-filtered';
import {
  ListWithPromiseState,
  listWithPromiseStateInit
} from '../../../global-state/database/common/list-with-status';
import { modelListOf } from '../../../global-state/database/model-list';
import { companyId$ } from '../../../global-state/router-id';
import { useLoadingPage } from '../../../global-state/show-loading-page';
import { routes } from '../../../routing/routes';
import { IModelType } from '../../../types/record/model';
import { NotFoundTemplate } from '../../shared/not-found-page-template';
import { ModelListView } from './model-list-view';

interface Props {
  koumutenId: number;
  companyId: number;
}

export const ModelList: FC<Props> = memo(
  ({ koumutenId, companyId }: Readonly<Props>) => {
    const navigator = useNavigator();

    const modelList$ = useDataStream<ListWithPromiseState<IModelType>>(
      listWithPromiseStateInit<IModelType>(),
      companyId$.pipe(switchMap(modelListOf))
    );

    const [
      createNewModelDialogIsOpen,
      openCreateNewModelDialog,
      closeCreateNewModelDialog
    ] = useBooleanState(false);

    const [
      editModelDialogIsOpen,
      openEditModelDialog,
      closeEditModelDialog
    ] = useBooleanState(false);

    const [
      deleteModelDialogIsOpen,
      openDeleteModelDialog,
      closeDeleteModelDialog
    ] = useBooleanState(false);

    const [modelToEdit, setModelToEdit] = useState<IModelType | undefined>(
      undefined
    );

    const [
      // dummy comment to control prettier
      modelToDelete,
      setModelToDelete
    ] = useState<IModelType | undefined>(undefined);

    const modelOnClick = useCallback(
      (modelId: number) => {
        navigator(routes.modelRoot(koumutenId, companyId, modelId));
      },
      [navigator, koumutenId, companyId]
    );

    const editModelButtonOnClick = useCallback(
      (model: IModelType) => {
        setModelToEdit(model);
        openEditModelDialog();
      },
      [setModelToEdit, openEditModelDialog]
    );

    const deleteModelButtonOnClick = useCallback(
      (model: IModelType) => {
        setModelToDelete(model);
        openDeleteModelDialog();
      },
      [setModelToDelete, openDeleteModelDialog]
    );

    const {
      searchString,
      onSearchStringChange,
      page,
      onPageChange,
      rowsPerPage,
      setRowsPerPage: onRowsPerPageChange,
      numRows,
      listFilteredCurrentPage: modelListFiltered
    } = useListFiltered(modelList$);

    useLoadingPage(modelListFiltered?.status === 'pending');

    return (
      <>
        {modelListFiltered === undefined ? (
          undefined
        ) : modelListFiltered.status === promiseState.error ? (
          <NotFoundTemplate message={viewTexts.notFoundMessages.modelList} />
        ) : (
          <ModelListView
            searchString={searchString}
            onSearchStringChange={onSearchStringChange}
            page={page}
            onPageChange={onPageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={onRowsPerPageChange}
            numRows={numRows}
            companyId={companyId}
            modelListFiltered={modelListFiltered.value.latest}
            modelOnClick={modelOnClick}
            createNewModelOnClick={openCreateNewModelDialog}
            createNewModelDialogOpen={createNewModelDialogIsOpen}
            createNewModelDialogOnClose={closeCreateNewModelDialog}
            editModelOnClick={editModelButtonOnClick}
            editModelDialogIsOpen={editModelDialogIsOpen}
            editModelDialogOnClose={closeEditModelDialog}
            modelToEdit={modelToEdit}
            deleteModelOnClick={deleteModelButtonOnClick}
            deleteModelDialogIsOpen={deleteModelDialogIsOpen}
            deleteModelDialogOnClose={closeDeleteModelDialog}
            modelToDelete={modelToDelete}
          />
        )}
      </>
    );
  }
);

ModelList.displayName = 'ModelList';
